/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — import-parser.ts
   File reading (CSV/Excel), header detection, raw row extraction,
   trial-balance row construction.
   ═══════════════════════════════════════════════════════════════════════ */

declare const XLSX: any;

// ─── Shared types ──────────────────────────────────────────────────────────

export interface TBRow {
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
  section?: string;
  priorDebit?: number;
  priorCredit?: number;
}

export interface TBResult extends Array<TBRow> {
  _hasComparative?: boolean;
}

// ─── Wizard column-mapping field definitions ──────────────────────────────

export interface WizardField {
  key: string;
  label: string;
  required: boolean;
  autoCandidates: string[];
}

export const WIZARD_FIELDS: WizardField[] = [
  { key: 'accountNumber', label: 'Account Number', required: false, autoCandidates: ['accountnumber','acctno','account','acct','code','id','accountno','number'] },
  { key: 'accountName',   label: 'Account Name',   required: true,  autoCandidates: ['accountname','name','description','desc','accountdescription'] },
  { key: 'debit',         label: 'Current Debit',   required: false, autoCandidates: ['debit','dr','debitamount'] },
  { key: 'credit',        label: 'Current Credit',  required: false, autoCandidates: ['credit','cr','creditamount'] },
  { key: 'balance',       label: 'Current Balance',   required: false, autoCandidates: ['balance','amount','currentbalance','endingbalance','currentamount','net'] },
  { key: 'priorDebit',    label: 'Prior Year Debit',  required: false, autoCandidates: ['priordebit','priordr','prioryeardebit','pydebit','beginningdebit'] },
  { key: 'priorCredit',   label: 'Prior Year Credit', required: false, autoCandidates: ['priorcredit','priorcr','prioryearcredit','pycredit','beginningcredit'] },
  { key: 'priorBalance',  label: 'Prior Year Balance',  required: false, autoCandidates: ['priorbalance','prioramount','prioryearbalance','beginningbalance','pybalance','pyamount'] },
  { key: 'section',       label: 'Section',         required: false, autoCandidates: ['section','category','classification','type','class'] },
];

// ─── CSV parser ────────────────────────────────────────────────────────────

export function parseCSV(text: string | null | undefined): string[][] {
  // Defensive: handle null/undefined/non-string inputs
  if (text == null) return [];
  text = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!text) return [];
  const firstLine = text.split('\n')[0];
  const delim = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ',';

  const rowsArr: string[][] = [];
  let curStr = '';
  let inQ = false;
  let fields: string[] = [];
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQ && text[i + 1] === '"') { curStr += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === delim && !inQ) {
      fields.push(curStr.trim()); curStr = '';
    } else if (ch === '\n' && !inQ) {
      fields.push(curStr.trim()); rowsArr.push(fields); fields = []; curStr = '';
    } else {
      curStr += ch;
    }
  }
  if (curStr.trim() || fields.length) { fields.push(curStr.trim()); rowsArr.push(fields); }
  return rowsArr;
}

export function normalizeHeader(h: string): string {
  return h.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e: ProgressEvent<FileReader>) => resolve(e.target!.result as string);
    r.onerror = reject;
    r.readAsText(file);
  });
}

export function findCol(headers: string[], candidates: string[]): number {
  for (const c of candidates) {
    const idx = headers.indexOf(c);
    if (idx !== -1) return idx;
  }
  return -1;
}

export function parseAmount(s: string | number | null | undefined): number {
  if (s == null) return 0;
  // Strip currency symbols, commas, whitespace, and percent signs
  // Also normalize unicode minus signs (U+2212, U+2013, U+2014) to ASCII minus
  let str = String(s).replace(/[−–—]/g, '-').replace(/[$£€¥¢,\s%]/g, '');
  const neg = str.startsWith('(') && str.endsWith(')');
  str = str.replace(/[()]/g, '');
  const n = parseFloat(str) || 0;
  return neg ? -n : n;
}

// ─── Trial-balance assembly ───────────────────────────────────────────────

export function parseTB(rowsArr: string[][], mode: string): TBResult {
  if (rowsArr.length < 1) throw new Error('Trial balance is empty.');
  const headers = rowsArr[0].map(normalizeHeader);
  const acctIdx  = findCol(headers, ['accountnumber','acctno','account','acct','code','id']);
  const nameIdx  = findCol(headers, ['accountname','name','description','desc']);
  const debitIdx = findCol(headers, ['debit','dr','debitamount']);
  const creditIdx= findCol(headers, ['credit','cr','creditamount']);
  const sectIdx  = mode === 'combined'
    ? findCol(headers, ['section','category','classification'])
    : -1;

  // Comparative columns (Prior Debit / Prior Credit)
  const priorDebitIdx = findCol(headers, ['priordebit','priordr','prioryeardebit','pydebit','beginningdebit']);
  const priorCreditIdx = findCol(headers, ['priorcredit','priorcr','prioryearcredit','pycredit','beginningcredit']);
  const hasComparative = priorDebitIdx !== -1 && priorCreditIdx !== -1;

  if (acctIdx  === -1) throw new Error('Trial balance missing "Account Number" column.');
  if (debitIdx === -1) throw new Error('Trial balance missing "Debit" column.');
  if (creditIdx=== -1) throw new Error('Trial balance missing "Credit" column.');

  const result: TBResult = [] as unknown as TBResult;
  for (let i = 1; i < rowsArr.length; i++) {
    const r = rowsArr[i];
    if (!r || r.every(c => !c)) continue;
    const acct   = (r[acctIdx]  || '').trim();
    const name   = nameIdx !== -1 ? (r[nameIdx] || '').trim() : acct;
    const debit  = parseAmount(r[debitIdx]  || '0');
    const credit = parseAmount(r[creditIdx] || '0');
    const sect   = sectIdx !== -1 ? (r[sectIdx] || '').trim().toLowerCase().replace(/\s+/g, '-') : undefined;
    const row: TBRow = { accountNumber: acct, accountName: name || acct, debit, credit, section: sect };
    if (hasComparative) {
      row.priorDebit = parseAmount(r[priorDebitIdx] || '0');
      row.priorCredit = parseAmount(r[priorCreditIdx] || '0');
    }
    if (acct || name) result.push(row);
  }
  result._hasComparative = hasComparative;
  return result;
}

/**
 * Convert raw wizard rows + a column mapping into a TBResult.
 */
export function wizardRowsToTB(rawRows: string[][], mapping: Record<string, number>): TBResult {
  const hasDebitCredit = mapping.debit !== undefined && mapping.credit !== undefined;
  const hasBalance = mapping.balance !== undefined;
  const hasPriorDC = mapping.priorDebit !== undefined && mapping.priorCredit !== undefined;
  const hasPriorBal = mapping.priorBalance !== undefined;

  if (!hasDebitCredit && !hasBalance) {
    throw new Error('Please map either Debit & Credit columns or a Balance column for the current year.');
  }
  if (mapping.accountName === undefined) {
    throw new Error('Please map the Account Name column.');
  }

  const result: TBResult = [] as unknown as TBResult;
  for (let i = 1; i < rawRows.length; i++) {
    const r = rawRows[i];
    if (!r || r.every(c => !c)) continue;

    const acctNum = mapping.accountNumber !== undefined ? (r[mapping.accountNumber] || '').trim() : String(i);
    const name = (r[mapping.accountName] || '').trim();
    if (!name && !acctNum) continue;

    let debit = 0;
    let credit = 0;
    if (hasDebitCredit) {
      debit = parseAmount(r[mapping.debit] || '0');
      credit = parseAmount(r[mapping.credit] || '0');
    } else if (hasBalance) {
      const bal = parseAmount(r[mapping.balance] || '0');
      if (bal >= 0) { debit = bal; credit = 0; }
      else { debit = 0; credit = Math.abs(bal); }
    }

    const sect = mapping.section !== undefined ? (r[mapping.section] || '').trim().toLowerCase().replace(/\s+/g, '-') : undefined;
    const row: TBRow = { accountNumber: acctNum, accountName: name || acctNum, debit, credit, section: sect };

    if (hasPriorDC) {
      row.priorDebit = parseAmount(r[mapping.priorDebit] || '0');
      row.priorCredit = parseAmount(r[mapping.priorCredit] || '0');
    } else if (hasPriorBal) {
      const pBal = parseAmount(r[mapping.priorBalance] || '0');
      if (pBal >= 0) { row.priorDebit = pBal; row.priorCredit = 0; }
      else { row.priorDebit = 0; row.priorCredit = Math.abs(pBal); }
    }

    result.push(row);
  }

  result._hasComparative = hasPriorDC || hasPriorBal;
  return result;
}
